import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import NewVacancyModal from './NewVacancyModal';
import PromptPreviewModal from './PromptPreviewModal';
import InvitePreviewModal from './InvitePreviewModal';
import type { Vacancy, VacancyStats } from '@/types';

interface VacancyDetailProps {
  vacancyId: string | null;
}

const statusLabels: Record<string, string> = {
  draft: 'Черновик',
  active: 'Активна',
  closed: 'Закрыта',
};

const statusColors: Record<string, string> = {
  draft: 'bg-secondary-100 text-secondary-700 border-secondary-200',
  active: 'bg-accent-100 text-accent-700 border-accent-200',
  closed: 'bg-background-200 text-foreground-500 border-background-300',
};

export default function VacancyDetail({ vacancyId }: VacancyDetailProps) {
  const [vacancy, setVacancy] = useState<Vacancy | null>(null);
  const [stats, setStats] = useState<VacancyStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profiles, setProfiles] = useState<{ id: string; name: string }[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [inviteMsg, setInviteMsg] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [inviteGenerating, setInviteGenerating] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [approving, setApproving] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [showInvitePreview, setShowInvitePreview] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!vacancyId) return;
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      api.get<Vacancy>(`/vacancies/${vacancyId}`),
      api.get<{ id: string; name: string }[]>('/employer-profiles').catch(() => []),
      api.get<VacancyStats>(`/vacancies/${vacancyId}/stats`).catch(() => null),
    ])
      .then(([vData, profs,  sData]) => {
        if (!cancelled) {
          setVacancy(vData);
          setStats(sData);
          setProfiles(profs || []);
          const vAny = vData as Vacancy & { employer_profile_ids?: string[]; employer_profile_id?: string; hh_invite_message?: string };
          setSelectedProfiles(vAny.employer_profile_ids || (vAny.employer_profile_id ? [vAny.employer_profile_id] : []));
          setInviteMsg(vAny.hh_invite_message || '');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Ошибка загрузки');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });


  return () => { cancelled = true; };
  }, [vacancyId, reloadFlag]);

  if (!vacancyId) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <p className="text-sm text-foreground-400">Выберите вакансию из списка</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 space-y-4">
        <div className="h-8 w-3/4 rounded-lg bg-background-100 animate-pulse"></div>
        <div className="h-4 w-1/2 rounded-lg bg-background-100 animate-pulse"></div>
        <div className="h-32 rounded-xl bg-background-100 animate-pulse mt-6"></div>
      </div>
    );
  }

  if (error || !vacancy) {
    return (
      <div className="p-8">
        <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {error || 'Вакансия не найдена'}
        </div>
      </div>
    );
  }

  const orgSubdomain = localStorage.getItem('hr_org_subdomain') || '';
  const landingUrl = vacancy.slug && orgSubdomain ? `https://${orgSubdomain}.starec.ai/${vacancy.slug}` : '';
  const resumeTotalWeight = vacancy.resume_criteria?.reduce((s, c) => s + (c.weight || 0), 0) || 0;
  const questionTotalWeight = vacancy.question_criteria?.reduce((s, c) => s + (c.weight || 0), 0) || 0;

  const generateInvite = async () => {
    if (!vacancyId || inviteGenerating) return;
    const focus = window.prompt('Пожелания к письму? (тон, акценты — можно оставить пустым)', '');
    if (focus === null) return;
    setInviteGenerating(true);
    try {
      const data = await api.post<{ message: string }>(`/vacancies/${vacancyId}/suggest-invite`, { focus: focus || null });
      setInviteMsg(data.message);
    } catch (err) {
      alert((err as Error).message || 'Ошибка AI-генерации');
    }
    setInviteGenerating(false);
  };

  const setInviteApproved = async (approved: boolean) => {
    if (!vacancyId || approving) return;
    if (approved && !inviteMsg.trim()) {
      alert('Сначала напишите или сгенерируйте текст письма');
      return;
    }
    setApproving(true);
    try {
      // Текст сохраняем вместе с решением — иначе утвердится не то, что на экране
      await api.put(`/vacancies/${vacancyId}`, {
        hh_invite_message: inviteMsg || null,
        hh_invite_approved: approved,
      });
      setReloadFlag((f) => f + 1);
    } catch (err) {
      alert((err as Error).message || 'Ошибка');
    }
    setApproving(false);
  };

  const saveProjectSettings = async () => {
    if (!vacancyId) return;
    setSettingsSaving(true);
    try {
    await api.put(`/vacancies/${vacancyId}`, {
    employer_profile_ids: selectedProfiles,
    employer_profile_id: selectedProfiles[0] || null,
    hh_invite_message: inviteMsg || null,
    ai_instructions: aiInstructions || null,
    });
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
    } catch (err) {
    alert((err as Error).message || 'Ошибка сохранения');
    }
    setSettingsSaving(false);
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">{vacancy.title}</h1>
            <p className="text-sm text-foreground-500">
              {vacancy.company_name}
              {vacancy.location && ` · ${vacancy.location}`}
              {vacancy.salary_from && ` · ${vacancy.salary_from.toLocaleString('ru-RU')}${vacancy.salary_to ? `–${vacancy.salary_to.toLocaleString('ru-RU')}` : ''} ₽`}
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <button
              onClick={() => setShowEdit(true)}
              className="px-3 py-1.5 rounded-lg bg-background-100 border border-background-200 text-foreground-700 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-edit-line" />
              Редактировать
            </button>
            <span className={`text-xs font-medium px-3 py-1 rounded-full border ${statusColors[vacancy.status] || statusColors.draft}`}>
              {statusLabels[vacancy.status] || vacancy.status}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate(`/candidates?vacancy=${vacancy.id}`)}
            className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-search-line"></i>
            Кандидаты
          </button>
          <button
            onClick={() => navigate(`/pipeline?vacancy=${vacancy.id}`)}
            className="px-4 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-kanban-view"></i>
            Pipeline
          </button>
          {vacancy.hh_vacancy_id && (
            <button
              onClick={() => navigate('/hhru')}
              className="px-4 py-2 rounded-lg bg-accent-100 text-accent-700 text-sm font-medium hover:bg-accent-200 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-global-line"></i>
              hh.ru
            </button>
          )}
          <button
            onClick={() => navigate(`/scoring?vacancy=${vacancy.id}`)}
            className="px-4 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-bar-chart-box-line"></i>
            Скоринг
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Object.entries(stats.by_status || {}).map(([key, count]) => (
            <div key={key} className="p-4 rounded-xl bg-white border border-background-200">
              <p className="text-2xl font-heading font-bold text-foreground-900">{count}</p>
              <p className="text-xs text-foreground-500 mt-0.5">{key}</p>
            </div>
          ))}
          <div className="p-4 rounded-xl bg-white border border-background-200">
            <p className="text-2xl font-heading font-bold text-primary-600">{stats.total}</p>
            <p className="text-xs text-foreground-500 mt-0.5">Всего кандидатов</p>
          </div>
        </div>
      )}

      {/* Настройки проекта: портреты руководителей + текст приглашения */}
      <div className="mb-6 bg-white rounded-xl border border-background-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground-800">Настройки проекта</h3>
          <div className="flex items-center gap-2">
            {settingsSaved && <span className="text-xs font-bold text-emerald-600"><i className="ri-check-line" /> Сохранено</span>}
            <button
              onClick={saveProjectSettings}
              disabled={settingsSaving}
              className="text-xs px-3 py-1.5 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 disabled:opacity-50 cursor-pointer"
            >
              {settingsSaving ? 'Сохраняю...' : 'Сохранить'}
            </button>
          </div>
        </div>
        <p className="text-xs text-foreground-500 mb-2">Портреты руководителей (совместимость считается с каждым отдельно):</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {profiles.map((p) => {
            const active = selectedProfiles.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => setSelectedProfiles((prev) => active ? prev.filter((x) => x !== p.id) : [...prev, p.id])}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                  active ? 'bg-amber-50 text-amber-700 border-amber-400' : 'border-background-200 text-foreground-500 hover:border-background-300'
                }`}
              >
                {active && <i className="ri-check-line mr-1" />}
                {p.name}
              </button>
            );
          })}
          {profiles.length === 0 && <span className="text-xs text-foreground-400">Портретов нет — создайте на вкладке «Портрет»</span>}
        </div>
        {/* Инструкция для AI: попадает в промпты генерации вопросов и оценки */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5 gap-3">
            <p className="text-xs text-foreground-500">
              Инструкция для AI по этой вакансии — что важно, чего избегать, на что смотреть в ответах.
              Она имеет приоритет над общими правилами:
            </p>
            <button
              onClick={() => setShowPrompt(true)}
              className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              <i className="ri-eye-line" />
              Показать промпт
            </button>
          </div>
          <textarea
            value={aiInstructions}
            onChange={(e) => setAiInstructions(e.target.value)}
            rows={4}
            placeholder="Например: работа физическая, кандидаты — без офисного опыта, пиши простым языком. Обязательно спроси про алкоголь косвенно. Не спрашивай про карьерные амбиции — здесь это не важно."
            className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
          />
        </div>

        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs text-foreground-500">Текст приглашения кандидатам в чат hh.ru — уходит автоматически каждому новому отклику (плейсхолдер {'{landing_url}'} заменится на ссылку анкеты):</p>
          <button
            onClick={generateInvite}
            disabled={inviteGenerating}
            className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-50 text-accent-700 hover:bg-accent-100 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap shrink-0 ml-3"
          >
            {inviteGenerating ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-sparkling-line" />}
            {inviteGenerating ? 'AI пишет...' : 'Сгенерировать с помощью AI'}
          </button>
        </div>
        <div className="flex justify-end mb-1.5">
          <button
            onClick={() => setShowInvitePreview(true)}
            className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-mail-check-line" />
            Проверить письмо перед отправкой
          </button>
        </div>
        <textarea
          value={inviteMsg}
          onChange={(e) => setInviteMsg(e.target.value)}
          rows={5}
          placeholder="Здравствуйте! Спасибо за отклик... Анкета: {landing_url} ..."
          className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
        />

        {/* Утверждение письма: без него рассылка кандидатам не идёт */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {vacancy.hh_invite_approved ? (
            <>
              <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1.5">
                <i className="ri-check-double-line" />
                Письмо утверждено — уходит каждому новому отклику
              </span>
              <button
                onClick={() => setInviteApproved(false)}
                disabled={approving}
                className="text-xs px-3 py-1.5 rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors disabled:opacity-50 cursor-pointer"
              >
                Приостановить рассылку
              </button>
            </>
          ) : (
            <>
              <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 flex items-center gap-1.5">
                <i className="ri-draft-line" />
                Черновик — кандидатам ничего не отправляется
              </span>
              <button
                onClick={() => setInviteApproved(true)}
                disabled={approving}
                className="text-xs px-3 py-1.5 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
              >
                {approving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
                Утвердить и включить рассылку
              </button>
            </>
          )}
        </div>
        <p className="text-xs text-foreground-400 mt-2">
          Любая правка текста снимает утверждение — письмо нужно утвердить заново.
        </p>
      </div>

      {/* Лендинг с анкетой */}
      {landingUrl && (
        <div className="mb-6 bg-white rounded-xl border border-background-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground-800 mb-0.5">Страница вакансии с анкетой</h3>
              <p className="text-xs text-foreground-500 break-all">{landingUrl}</p>
              <p className="text-xs text-foreground-400 mt-1">
                Эту ссылку получает кандидат вместо {'{landing_url}'} — на ней описание вакансии и {vacancy.question_criteria?.length || 0} вопросов анкеты.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigator.clipboard?.writeText(landingUrl)}
                className="text-xs px-3 py-2 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-file-copy-line" />
                Скопировать
              </button>
              <a
                href={landingUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs px-3 py-2 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-external-link-line" />
                Открыть
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Description */}
      {vacancy.description && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-foreground-800 mb-2">Описание</h3>
          <div className="text-sm text-foreground-600 leading-relaxed bg-white rounded-xl p-4 border border-background-200 whitespace-pre-wrap">
            {vacancy.description}
          </div>
        </div>
      )}

      {/* Resume Criteria */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground-800">Критерии резюме</h3>
          <span className="text-xs text-purple-600 font-medium bg-purple-50 px-2 py-0.5 rounded-full">
            Сумма весов: {resumeTotalWeight}
          </span>
        </div>
        {vacancy.resume_criteria?.length > 0 ? (
          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Критерий</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Описание</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-foreground-500 w-20">Вес</th>
                </tr>
              </thead>
              <tbody>
                {vacancy.resume_criteria.map((c) => (
                  <tr key={c.id} className="border-b border-background-100 last:border-0">
                    <td className="px-4 py-3 text-foreground-800 font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-foreground-500">{c.description || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-purple-700 font-semibold text-xs bg-purple-50 px-2 py-0.5 rounded-full">
                        {c.weight}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-foreground-400">Критерии не заданы</p>
        )}
      </div>

      {/* Question Criteria */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground-800">Вопросы анкеты</h3>
          <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-full">
            Сумма весов: {questionTotalWeight}
          </span>
        </div>
        {vacancy.question_criteria?.length > 0 ? (
          <div className="space-y-3">
            {vacancy.question_criteria.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-background-200 p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="font-medium text-sm text-foreground-800">{c.name}</h4>
                  <span className="shrink-0 text-xs text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">
                    Вес: {c.weight}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 mb-2">{c.question}</p>
                {c.eval_criteria && (
                  <p className="text-xs text-foreground-400">Критерии оценки: {c.eval_criteria}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-foreground-400">Вопросы не заданы</p>
        )}
      </div>

      {showEdit && (
        <NewVacancyModal
          vacancy={vacancy}
          onClose={() => setShowEdit(false)}
          onCreated={() => {
            setShowEdit(false);
            setReloadFlag((f) => f + 1);
          }}
        />
      )}

      {showPrompt && vacancyId && (
        <PromptPreviewModal vacancyId={vacancyId} onClose={() => setShowPrompt(false)} />
      )}

      {showInvitePreview && vacancyId && (
        <InvitePreviewModal vacancyId={vacancyId} onClose={() => setShowInvitePreview(false)} />
      )}
    </div>
  );
}
